import { useEffect, useRef, useState } from 'react';

import {
  CodeRefused,
  Session,
  assertAccountMatch,
  rememberedAccountId,
  requestCode,
  requestMagicLink,
  signInHotel,
  storeAccountId,
  verifyCode,
} from '../auth';
import { go, goReplace } from '../App';
import { resolveAccount } from '../config';
import { CODE_LABEL, CODE_ON_ITS_WAY, GREEK_MOBILES_ONLY, NEW_CODE_ON_ITS_WAY } from '../joining';
import { Segmented } from '../ui';
import { rememberProven } from '../invitation';

// The pending SMS challenge survives a refresh, so the code-entry screen
// can be a route (canon: every screen has a URL). The challengeId is opaque
// and single-purpose; the code still only ever arrives by SMS.
//
// B34: it is replaced after every wrong try (the API hands back the next live
// one — a mistyped digit is just typed again, three tries per code), it marks a
// code that is finished (three misses, or its five minutes up), and it carries
// when the code was sent, so "Send again" waits the API's 30 seconds even
// across a refresh.
const PENDING_KEY = 'iso.pendingSms';
interface PendingSms {
  challengeId: string;
  phone: string;
  // The one line every number gets (DECISION-080 §10), as the API said it.
  message?: string;
  sentAt?: number;
  resendAfter?: number;
  sends?: number;
  finished?: boolean;
}
function readPending(): PendingSms | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as PendingSms) : null;
  } catch {
    return null;
  }
}

// The number the code went to, grouped the way the join page's example is
// ("+30 691 234 5678"), so it can be checked at a glance. Anything that isn't a
// Greek mobile is shown as the API read it.
function grouped(e164: string): string {
  const m = /^\+30(69\d)(\d{3})(\d{4})$/.exec(e164);
  return m ? `+30 ${m[1]} ${m[2]} ${m[3]}` : e164;
}

const seconds = (n: number) => `${n} ${n === 1 ? 'second' : 'seconds'}`;

// Android Chrome reads the code out of the text (WebOTP), after asking; the
// text's last line, "@host #code", is what it matches. Anything else resolves
// to null and the field's own one-time-code suggestion still works.
function readTextedCode(signal: AbortSignal): Promise<string | null> {
  if (!('OTPCredential' in window)) return Promise.resolve(null);
  try {
    const get = navigator.credentials.get.bind(navigator.credentials) as (
      o: unknown,
    ) => Promise<{ code?: string } | null>;
    return get({ otp: { transport: ['sms'] }, signal })
      .then((c) => c?.code ?? null)
      .catch(() => null);
  } catch {
    return Promise.resolve(null);
  }
}

// The entry shell: no rail, no top bar — an ink panel beside a plain form.
// The ink panel does the brand work so the form can be plain. For hotels the
// account id comes FIRST — pre-auth it selects pool and client through the
// runtime map. Its shape is checked here, never its existence: ids are derived
// at provisioning, so an operator-minted hotel is one this bundle has never
// heard of (B2), and the credential is what decides — a client-side "not
// found" both refused real tenants and answered the existence question before
// authentication. Suppliers sign in by phone alone: the server resolves the
// tenant from the number (canon 11 / DECISION-059), so the SMS tab shows no
// account-id field. One generic failure message, never per-field.
//
// B35: the ink panel shows no figures. It carried a fixed "6 min · 94% · Rhodes ·
// August" — made-up numbers on a public page — and shows none until a ruling
// allows public figures. B31 + DECISION-080 §8/§10: the phone form says only
// Greek mobiles get codes for now, and the code screen says the one line every
// number gets, so the page never tells anyone whether an account exists.
export function SignIn({ segments, onSignedIn }: { segments: string[]; onSignedIn: (s: Session) => void }) {
  const [accountId, setAccountId] = useState(rememberedAccountId());
  const [remember, setRemember] = useState(!!rememberedAccountId());
  const [mode, setMode] = useState<'password' | 'sms'>('password');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [linkSentTo, setLinkSentTo] = useState('');

  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [codeNote, setCodeNote] = useState('');
  const [now, setNow] = useState(() => Date.now());

  // /signin/code — the SMS-code entry screen, a route not a state flag.
  const [pending, setPendingState] = useState<PendingSms | null>(readPending);
  const savePending = (p: PendingSms | null) => {
    if (p) sessionStorage.setItem(PENDING_KEY, JSON.stringify(p));
    else sessionStorage.removeItem(PENDING_KEY);
    setPendingState(p);
  };
  const codeView = segments[0] === 'signin' && segments[1] === 'code';
  useEffect(() => {
    if (codeView && !pending) goReplace('/signin');
    // Landing on the code route puts the form on the SMS tab it belongs to.
    if (codeView) setMode('sms');
  }, [codeView, pending]);

  // "Send again" waits the API's resendAfterSeconds from the send — or not at
  // all once this code is finished (B34). The tick runs only while waiting.
  const waitLeft = pending
    ? Math.max(0, Math.ceil(((pending.sentAt ?? 0) + (pending.resendAfter ?? 30) * 1000 - now) / 1000))
    : 0;
  const counting = codeView && !!pending && !pending.finished && waitLeft > 0;
  useEffect(() => {
    if (!counting) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [counting]);
  const canResend = !!pending && (!!pending.finished || waitLeft === 0);

  const sendCode = async (to: string, again: boolean) => {
    const sent = await requestCode(to);
    savePending({
      challengeId: sent.challengeId,
      phone: sent.phone,
      message: sent.message,
      sentAt: Date.now(),
      resendAfter: sent.resendAfterSeconds,
      sends: again ? (pending?.sends ?? 1) + 1 : 1,
    });
    setNow(Date.now());
    setCode('');
    // After Send again the welcome line says a new code, in place of the first
    // (never a second line under it).
    setCodeNote('');
  };

  // A try (B34): signed in; a wrong code, typed again against the live try the
  // API handed back; or a finished code, whose only way on is a new one. The
  // guard is a ref, not `busy`: a WebOTP fill and an Enter in the same tick would
  // both pass a state check, and the second would replay a spent challenge.
  const checking = useRef(false);
  const checkCode = async (typed: string) => {
    if (!pending || checking.current) return;
    checking.current = true;
    setBusy(true);
    setError('');
    setCodeNote('');
    try {
      const s = await verifyCode(pending.challengeId, typed);
      sessionStorage.removeItem(PENDING_KEY);
      // N40: an invited number with no account — its invitation opens next,
      // phone proven (canon 09: "Invited → the invitation screen").
      if ('invitation' in s) {
        rememberProven(s.invitation, s.challengeId);
        go(`/join?t=${encodeURIComponent(s.invitation)}`);
        return;
      }
      done(s);
    } catch (e) {
      setCode('');
      if (e instanceof CodeRefused) {
        setError(e.message);
        savePending(e.kind === 'wrong' ? { ...pending, challengeId: e.challengeId } : { ...pending, finished: true });
        // After Send again, a miss may be the earlier code from a text that came late.
        if (e.kind === 'wrong' && (pending.sends ?? 1) > 1) setCodeNote('If two texts came, try the code in the other one.');
      } else {
        setError((e as Error).message);
      }
    } finally {
      checking.current = false;
      setBusy(false);
    }
  };
  const checkLatest = useRef(checkCode);
  checkLatest.current = checkCode;

  // Android fills the code in from the text itself, and the try goes at once.
  const waitingForText = codeView && !!pending && !pending.finished;
  const sendsSoFar = pending?.sends ?? 1;
  useEffect(() => {
    if (!waitingForText) return;
    const ac = new AbortController();
    void readTextedCode(ac.signal).then((c) => {
      if (!c) return;
      setCode(c);
      void checkLatest.current(c);
    });
    return () => ac.abort();
  }, [waitingForText, sendsSoFar]);

  const gate = (): boolean => {
    // Suppliers sign in by phone; the server resolves the tenant from the number,
    // so the SMS path needs no account id (canon 11 / DECISION-059, finding ①).
    if (mode === 'sms') return true;
    // Shape only. This gate also fronts the magic-link button below, and that
    // link is a minted hotel's FIRST sign-in — the operator console creates
    // them with no password at all (api/src/adapters/users.ts) — so a refusal
    // here closes the only door they have.
    if (!resolveAccount(accountId)) {
      setError('The account id is 15 digits, starting with 2.');
      return false;
    }
    storeAccountId(accountId, remember);
    return true;
  };

  const run = async (fn: () => Promise<void>) => {
    if (!gate()) return;
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Hotels typed an account id, so the token must match it; suppliers signed in
  // by phone with no account id to match against — the token's tenant is truth.
  const done = (s: Session) => onSignedIn(mode === 'sms' ? s : assertAccountMatch(s, accountId));

  return (
    <div className="signin-wrap">
      <aside className="signin-ink">
        <div className="logo">ISO</div>
        <div className="mid">
          <h2>Every experience your guests book, and every operator who delivers it.</h2>
          <p className="sub">One place for hotels and their suppliers.</p>
        </div>
        <p className="sub" style={{ fontSize: 'var(--text-sm)' }}>
          New supplier with an invitation? Open the link the hotel sent you — there is no password to create.
        </p>
      </aside>

      <div className="signin-form-side">
        <div className="signin">
          <h1>Sign in</h1>
          <p className="welcome">Welcome back. We will take you to your side of the platform.</p>

          {!linkSentTo && (
            <>
              {mode === 'password' && (
                <div className="field">
                  <label htmlFor="accountId">Account id</label>
                  <input
                    id="accountId"
                    inputMode="numeric"
                    maxLength={15}
                    placeholder="15 digits"
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value.replace(/\D/g, ''))}
                    className="mono"
                    style={{ letterSpacing: '0.08em' }}
                    required
                  />
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, fontWeight: 400 }}>
                    <input type="checkbox" style={{ width: 'auto', height: 'auto' }} checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                    Remember this account id on this machine
                  </label>
                </div>
              )}

              <Segmented
                value={mode}
                onChange={setMode}
                options={[
                  { value: 'password', label: 'Email & password' },
                  { value: 'sms', label: 'Phone & SMS code' },
                ]}
              />
            </>
          )}

          {linkSentTo ? (
            // The "Magic link sent" entry screen: the machine waits for the
            // inbox. Nothing here reveals whether the account exists.
            <div>
              <h2 style={{ fontSize: 'var(--text-2xl)', color: 'var(--text-primary)', marginBottom: 8 }}>Check your email</h2>
              <p className="welcome">
                If {linkSentTo} has an account here, a sign-in link is on its way. Opening it signs
                this browser in — it works for 15 minutes.
              </p>
              <button
                type="button"
                className="btn ghost"
                style={{ width: '100%', marginTop: 12 }}
                disabled={busy}
                onClick={() => run(async () => requestMagicLink(linkSentTo))}
              >
                Send it again
              </button>
              <button type="button" className="btn secondary" style={{ width: '100%', marginTop: 8 }} onClick={() => setLinkSentTo('')}>
                Back to sign in
              </button>
            </div>
          ) : mode === 'password' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                run(async () => done(await signInHotel(email, password)));
              }}
            >
              <div className="field">
                <label htmlFor="email">Email</label>
                <input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div className="field">
                <label htmlFor="password">Password</label>
                <input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <button className="btn lg" disabled={busy} style={{ width: '100%' }}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
              <button
                type="button"
                className="btn ghost"
                style={{ width: '100%', marginTop: 8 }}
                disabled={busy}
                onClick={() => {
                  if (!email) {
                    setError('Enter your email first — the link goes there.');
                    return;
                  }
                  run(async () => {
                    await requestMagicLink(email);
                    setLinkSentTo(email);
                  });
                }}
              >
                Email me a sign-in link instead
              </button>
            </form>
          ) : codeView && pending ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void checkCode(code);
              }}
            >
              {/* The number, grouped, then the one line every number gets — never
                  "the code we sent": for a number with no account, or one whose
                  text AWS refused, nothing went. */}
              <p className="welcome" role="status">
                {grouped(pending.phone)}
                <br />
                {(pending.sends ?? 1) > 1 ? NEW_CODE_ON_ITS_WAY : (pending.message ?? CODE_ON_ITS_WAY)}
              </p>
              <div className="field">
                <label htmlFor="code">{CODE_LABEL}</label>
                {/* One field takes the whole code — typed, pasted ("482 913"
                    included) or suggested by the phone (B34). */}
                <input
                  id="code"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  autoComplete="one-time-code"
                  value={code}
                  disabled={!!pending.finished}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  className="mono"
                  style={{ fontSize: 'var(--text-2xl)', letterSpacing: '0.4em', textAlign: 'center', height: 52 }}
                />
              </div>
              {codeNote && (
                <p className="hint" role="status" style={{ marginBottom: 'var(--space-sm)' }}>
                  {codeNote}
                </p>
              )}
              {/* One primary: the step that moves things on (canon 09, panel
                  rule 2). Once a code is finished that is Send again, so it
                  takes the primary look and Sign in steps back — as the phone does. */}
              <button
                className={pending.finished ? 'btn secondary lg' : 'btn lg'}
                disabled={busy || code.length !== 6 || !!pending.finished}
                style={{ width: '100%' }}
              >
                {busy ? 'Checking…' : 'Sign in'}
              </button>
              {canResend ? (
                <button
                  type="button"
                  className={pending.finished ? 'btn lg' : 'btn ghost'}
                  style={{ width: '100%', marginTop: 'var(--space-sm)' }}
                  disabled={busy}
                  onClick={() => run(() => sendCode(pending.phone, true))}
                >
                  Send again
                </button>
              ) : (
                <p className="hint" style={{ marginTop: 'var(--space-sm)' }}>
                  You can ask for a new code in {seconds(waitLeft)}.
                </p>
              )}
            </form>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                run(async () => {
                  await sendCode(phone, false);
                  go('/signin/code');
                });
              }}
            >
              <div className="field">
                <label htmlFor="phone">Phone number</label>
                <input id="phone" type="tel" placeholder="+30 …" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\s/g, ''))} required />
                <p className="hint" style={{ marginTop: 6 }}>
                  Suppliers sign in with a 6-digit SMS code — no password exists for them. {GREEK_MOBILES_ONLY}
                </p>
              </div>
              <button className="btn lg" disabled={busy} style={{ width: '100%' }}>
                {busy ? 'Sending…' : 'Send the code'}
              </button>
            </form>
          )}

          {error && <p className="error-line" role="alert">{error}</p>}
        </div>
      </div>
    </div>
  );
}
